import React from "react";
import { Alert, Form, Input, Modal, Select } from "antd";
import { useForm } from "antd/es/form/Form";
import { useTranslation } from "react-i18next";
import {
  useInviteUserForPartnerMutation,
  useGetAllMyStoresQuery,
} from "../graphql/generated/hooks";

const ERROR_CODE_TRANSLATION_MAP: Record<string, string> = {
  USER_ALREADY_INVITED: "message.error.user_already_invited",
};

function getGraphQLErrorCode(error: unknown): string | undefined {
  if (!error || typeof error !== "object") return undefined;

  // ApolloError shape: { graphQLErrors, networkError, ... }
  const apolloError = error as {
    graphQLErrors?: Array<{ extensions?: { code?: unknown } }>;
    networkError?: unknown;
  };

  const graphQLErrorCode = apolloError.graphQLErrors?.[0]?.extensions?.code;
  if (typeof graphQLErrorCode === "string") return graphQLErrorCode;

  // When server responds with non-200 (e.g. 400), Apollo may put GQL errors in networkError.result.errors
  const networkError = apolloError.networkError as
    | { result?: { errors?: Array<{ extensions?: { code?: unknown } }> } }
    | undefined;
  const networkGraphQLErrorCode =
    networkError?.result?.errors?.[0]?.extensions?.code;
  if (typeof networkGraphQLErrorCode === "string") return networkGraphQLErrorCode;

  return undefined;
}

const InviteUserModal: React.FC<{
  isOpen: boolean;
  close: () => void;
  onInvited?: () => unknown | Promise<unknown>;
}> = ({
  isOpen,
  close,
  onInvited,
}) => {
  const { t } = useTranslation();
  const [form] = useForm();
  const [inviteUser, { data, loading, error, reset }] =
    useInviteUserForPartnerMutation();
  const { data: storesData, loading: loadingStores } = useGetAllMyStoresQuery();

  const onFinish = async (values: any) => {
    try {
      await inviteUser({
        variables: {
          email: values.email,
          storeId: values.storeId || undefined,
        },
      });
      await onInvited?.();
    } catch {
      // Error is already captured in the `error` state from the mutation hook
    }
  };

  const errorCode = getGraphQLErrorCode(error);
  const errorMessageKey =
    errorCode && ERROR_CODE_TRANSLATION_MAP[errorCode]
      ? ERROR_CODE_TRANSLATION_MAP[errorCode]
      : "message.error.invitation_failed";

  return (
    <Modal
      title={t("title.invite_user")}
      open={isOpen}
      onOk={() => {
        form.submit();
      }}
      onCancel={() => {
        close();
        form.resetFields();
        reset();
      }}
      confirmLoading={loading}
    >
      {data && (
        <Alert
          message={t("message.success.invitation_sent")}
          description={
            <a
              href={`${window.location.origin}/invite/${data.inviteUserForPartner}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {`${window.location.origin}/invite/${data.inviteUserForPartner}`}
            </a>
          }
          type="success"
          showIcon
          closable
          onClose={() => {
            close();
            form.resetFields();
            reset();
          }}
        />
      )}
      {error && (
        <Alert
          message={t(errorMessageKey)}
          type="error"
          showIcon
          closable
          onClose={() => {
            form.resetFields();
            reset();
          }}
        />
      )}
      <Form
        form={form}
        onFinish={onFinish}
        layout="vertical"
        style={{ marginTop: "20px" }}
      >
        <Form.Item
          rules={[
            { required: true, message: t("message.error.field_required") },
            { type: "email", message: t("message.error.invalid_email") },
          ]}
          hasFeedback
          name="email"
          label={t("label.email")}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="storeId"
          label={t("label.store") || "Store"}
        >
          <Select
            loading={loadingStores}
            placeholder={t("placeholder.select_stores")}
            allowClear
            options={[
              { value: null, label: t("label.all_stores") },
              ...(storesData?.getAllStoresForPartner?.map((store) => ({
                value: store.id,
                label: store.name,
              })) ?? []),
            ]}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default InviteUserModal;
